import { nanoid } from 'nanoid'
import * as yup from 'yup'

import {
  EActionStepType,
  TActionApiCallStep,
  TActionStep,
} from '~/features/scenarios/components/createScenarioFlow/createCustomScenarioFlow/customScenarioStepThree/types'
import { DEFAULT_SCENARIO_NAME } from '~/features/scenarios/constants'
import {
  EFormParameterListKeys,
  EParameterListKeys,
  EScenarioActionKeys,
  EScenarioActionMethod,
  EScenarioActionUsesTemplate,
  EScenarioAgentKeys,
  EScenarioKeys,
  TAgentCustomField,
  TFormParameterListContent,
  TFormScenarioActionPayload,
  TInstructionStep,
  TParameterListContent,
  TScenario,
  TScenarioAction,
  TScenarioActionPayload,
  TScenarioAgent,
  TScenarioAgentCustomFields,
  TScenarioPayload,
  TUpdateScenarioPayload,
  TUpdateScenarioTitlePayload,
  EFormAgentCustomFieldKeys,
  TScenarioActionProps,
} from '~/features/scenarios/models'
import { EClientPath, EServerPath } from '~/models/api'
import pathBuilder from '~/utils/api'

export const queryKeys = {
  scenarios: (orgId: string) => [EServerPath.scenarios, orgId],
  scenario: (id: string) => [EServerPath.scenario, id],
  scenarioAgents: (scenarioId: string) => [
    EServerPath.scenarioAgents,
    scenarioId,
  ],
  scenarioAgentsForAdmin: (scenarioId: string) => [
    EServerPath.scenarioAgentsForAdmin,
    scenarioId,
  ],
  scenarioAgent: (scenarioId: string, agentId: string) => [
    EServerPath.scenarioAgent,
    scenarioId,
    agentId,
  ],
  scenarioActions: () => [EServerPath.actions],
  scenarioAction: (actionId: string) => [EServerPath.action, actionId],
  actions: (orgId: string) => [EServerPath.actions, orgId],
  action: (id: string) => [EServerPath.action, id],
}

export const scenarioTitleValidationSchema = yup.object({
  [EScenarioKeys.name]: yup.string().required('Scenario name is required'),
})

export const scenarioValidationSchema = yup.object({
  [EScenarioKeys.name]: yup
    .string()
    .min(3, 'Name must be at least 3 characters long'),
  [EScenarioKeys.instructionSteps]: yup.string().optional(),
  [EScenarioKeys.actionIds]: yup.array().optional(),
  [EScenarioKeys.additionalInstructions]: yup.string().optional(),
  [EScenarioKeys.channels]: yup.array().optional(),
  [EScenarioKeys.triggerInstructions]: yup.string().optional(),
  [EScenarioKeys.agentCustomFields]: yup.array().of(
    yup.object({
      [EFormAgentCustomFieldKeys.key]: yup.string().required('Key is required'),
      [EFormAgentCustomFieldKeys.type]: yup
        .string()
        .required('Type is required'),
      [EFormAgentCustomFieldKeys.description]: yup.string().optional(),
      [EFormAgentCustomFieldKeys.required]: yup
        .boolean()
        .required('Some value is required'),
    }),
  ),
})

export const pathForScenario = (scenarioId: string) => {
  return pathBuilder(EClientPath.scenarioCreateEdit, { scenarioId })
}

export const isDisabledAgent = (
  scenario: TScenario | null | undefined,
  agent: TScenarioAgent,
) =>
  Object.entries(scenario?.[EScenarioKeys.agentCustomFields] || []).some(
    ([key, value]) => value.required && !agent.customFields?.[key],
  )

export const getAssistantCheckboxTooltipTitle = (
  scenario: TScenario | null | undefined,
  agent: TScenarioAgent,
) =>
  isDisabledAgent(scenario, agent)
    ? 'Required details must be populated to activate scenarios for each assistant.'
    : null

export const getScenarioAgentValidationSchema = (
  allCustomFields: Record<string, TAgentCustomField> | undefined,
) =>
  yup.object({
    [EScenarioAgentKeys.active]: yup.boolean().required(),
    [EScenarioAgentKeys.customFields]: yup
      .object()
      .shape(
        Object.entries(allCustomFields || []).reduce(
          (acc, [key, value]) => {
            let fieldSchema: yup.Schema

            switch (typeof value.type) {
              case 'string':
                fieldSchema = yup.string()
                break
              case 'boolean':
                fieldSchema = yup.boolean()
                break
              case 'number':
                fieldSchema = yup.number()
                break
              default:
                throw new Error(`Unsupported field type: ${typeof value.type}`)
            }

            acc[key] = fieldSchema.nullable()
            return acc
          },
          {} as Record<string, yup.Schema<TScenarioAgentCustomFields>>,
        ),
      )
      .nullable(),
    [EScenarioAgentKeys.timezone]: yup.string().nullable(),
  })

export const pathForScenarioAction = (actionId: string) => {
  return pathBuilder(EClientPath.scenarioActionCreateEdit, {
    actionId,
  })
}

export const scenarioActionTitleValidationSchema = yup.object({
  [EScenarioActionKeys.name]: yup
    .string()
    .matches(/^[a-zA-Z_]+$/, {
      message: 'Only letters and underscore are allowed',
    })
    .required('Action name is required'),
})

const parameterListSchema = {
  [EFormParameterListKeys.name]: yup
    .string()
    .max(60, 'Name is too long')
    .required('Name is required'),
  [EFormParameterListKeys.description]: yup
    .string()
    .max(191, 'Description is too long')
    .optional(),
  [EFormParameterListKeys.type]: yup
    .mixed()
    .oneOf(['string', 'number', 'boolean', 'object', 'array'], 'Invalid type')
    .required('Type is required'),
  [EFormParameterListKeys.isOptional]: yup
    .boolean()
    .required('Required status must be specified'),
}

export const scenarioActionValidationSchema = yup.object({
  [EScenarioActionKeys.parametersList]: yup
    .array(
      yup.object().shape({
        ...parameterListSchema,
        [EFormParameterListKeys.properties]: yup
          .array(
            yup.object().shape({
              ...parameterListSchema,
            }),
          )
          .when([EFormParameterListKeys.type], {
            is: (type: string) => type === 'object' || type === 'array',
            then: (schema) =>
              schema
                .required('You must add at least one property')
                .min(1, 'You must add at least one property'),
            otherwise: (schema) => schema.optional(),
          }),
      }),
    )
    .optional(),
  [EScenarioActionKeys.waitMessage]: yup
    .string()
    .max(191, 'Wait message is too long')
    .optional(),
  [EScenarioActionKeys.failureMessage]: yup
    .string()
    .required('Error message is required'),
  [EScenarioActionKeys.allPropertiesRequired]: yup.boolean().optional(),
  [EScenarioActionKeys.usesTemplate]: yup
    .mixed()
    .oneOf(Object.values(EScenarioActionUsesTemplate))
    .optional(),
  [EScenarioActionKeys.webhook]: yup
    .string()
    .url('Invalid URL format')
    .optional(),
  [EScenarioActionKeys.description]: yup
    .string()
    .max(191, 'Description is too long')
    .optional(),
  [EScenarioActionKeys.method]: yup
    .mixed()
    .oneOf(Object.values(EScenarioActionMethod))
    .optional(),
  [EScenarioActionKeys.authHeader]: yup.string().optional(),
  [EScenarioActionKeys.apiKey]: yup
    .string()
    .when(EScenarioActionKeys.authHeader, {
      is: (authHeader: string) => !!authHeader,
      then: (schema) =>
        schema.required('API Key is required when Auth Header is provided'),
    }),
  [EScenarioActionKeys.headers]: yup
    .array(
      yup.object().shape({
        key: yup
          .string()
          .matches(/^\S*$/, 'Key cannot contain spaces')
          .required('Key is required'),
        value: yup.string().required('Value is required'),
      }),
    )
    .test('unique-keys', 'Keys must be unique', (headers) => {
      if (!headers) {
        return true
      }

      const keys = headers.map((item) => item.key)
      return new Set(keys).size === keys.length
    })
    .optional(),
})

const createScenarioInstructionStepValidationSchema = yup.object().shape({
  description: yup.string().required('Description is required'),
})

export const createScenarioStepsFormValidationSchema = yup.object().shape({
  actionSteps: yup.array().of(
    yup.lazy((value) => {
      if (
        value.type === EActionStepType.API_CALL &&
        value.enableNewApiActions
      ) {
        return scenarioActionTitleValidationSchema
          .concat(scenarioActionValidationSchema)
          .concat(createScenarioInstructionStepValidationSchema)
      }

      return createScenarioInstructionStepValidationSchema
    }),
  ),
})

export const createScenarioCustomFieldsValidationSchema = yup.object({
  scenarioRules: yup.string(),
  agentCustomFields: yup.array().of(
    yup.object({
      key: yup.string().required('Key is required'),
      type: yup.string().required('Type is required'),
      description: yup.string(),
    }),
  ),
})

export const getScenarioActionTitlePayloadWithDefaults = (
  scenarioAction?: Partial<TScenarioActionPayload> | null,
): TUpdateScenarioTitlePayload => ({
  [EScenarioActionKeys.name]: scenarioAction?.[EScenarioActionKeys.name] || '',
})

export const getScenarioActionPayloadWithDefaults = (
  scenarioAction?: Partial<TScenarioActionPayload> | null,
): TFormScenarioActionPayload => ({
  [EScenarioActionKeys.name]: scenarioAction?.name || '',
  [EScenarioActionKeys.description]:
    scenarioAction?.[EScenarioActionKeys.description] || '',
  [EScenarioActionKeys.waitMessage]:
    scenarioAction?.[EScenarioActionKeys.waitMessage] || '',
  [EScenarioActionKeys.webhook]:
    scenarioAction?.[EScenarioActionKeys.webhook] || '',
  [EScenarioActionKeys.parametersList]: Object.entries(
    scenarioAction?.[EScenarioActionKeys.parametersList] || {},
  ).map(
    ([key, value]) =>
      ({
        [EFormParameterListKeys.id]: nanoid(),
        [EFormParameterListKeys.name]: key,
        [EFormParameterListKeys.description]:
          value[EParameterListKeys.description],
        [EFormParameterListKeys.isOptional]:
          value[EParameterListKeys.type].includes('null'),
        [EFormParameterListKeys.type]:
          typeof value[EParameterListKeys.type] === 'string'
            ? value[EParameterListKeys.type]
            : value[EParameterListKeys.type][0],
      }) as TFormParameterListContent,
  ),
  [EScenarioActionKeys.failureMessage]:
    scenarioAction?.[EScenarioActionKeys.failureMessage] || '',
  allPropertiesRequired:
    scenarioAction?.[EScenarioActionKeys.allPropertiesRequired] ?? true,
  usesTemplate:
    scenarioAction?.[EScenarioActionKeys.usesTemplate] ||
    EScenarioActionUsesTemplate.NO,
  [EScenarioActionKeys.method]:
    scenarioAction?.[EScenarioActionKeys.method] || undefined,
  [EScenarioActionKeys.authHeader]:
    scenarioAction?.[EScenarioActionKeys.authHeader] || '',
  [EScenarioActionKeys.apiKey]:
    scenarioAction?.[EScenarioActionKeys.apiKey] || '',
  [EScenarioActionKeys.headers]:
    Object.entries(
      JSON.parse(scenarioAction?.[EScenarioActionKeys.headers] || '{}'),
    ).reduce(
      (
        acc: { key: string; value: string }[],
        [key, value]: [string, unknown],
      ) => {
        acc.push({ key, value: value as string })
        return acc
      },
      [] as { key: string; value: string }[],
    ) || [],
})

export const getScenarioActionFormPayloadForApi = (
  values: TFormScenarioActionPayload,
): TScenarioActionPayload => ({
  ...values,
  [EScenarioActionKeys.headers]: JSON.stringify(
    (values[EScenarioActionKeys.headers] || []).reduce(
      (
        acc: { [x: string]: string },
        currentItem: { key: string; value: string },
      ) => {
        acc[currentItem.key] = currentItem.value
        return acc
      },
      {} as { [x: string]: string },
    ),
  ),
  [EScenarioActionKeys.parametersList]: (
    values[EScenarioActionKeys.parametersList] || []
  ).reduce(
    (acc, currentItem) => {
      acc[currentItem[EFormParameterListKeys.name]] = {
        [EParameterListKeys.description]:
          currentItem[EFormParameterListKeys.description],
        [EParameterListKeys.required]: true,
        [EParameterListKeys.type]: currentItem[
          EFormParameterListKeys.isOptional
        ]
          ? [currentItem[EFormParameterListKeys.type], 'null']
          : currentItem[EFormParameterListKeys.type],
      }

      return acc
    },
    {} as Record<string, TParameterListContent>,
  ),
})

const getObjectOfPropertiesFromArrayOfProperties = (
  values: TFormParameterListContent[],
) => {
  return values.reduce(
    (acc, currentItem) => {
      acc[currentItem[EFormParameterListKeys.name]] = {
        [EParameterListKeys.description]:
          currentItem[EFormParameterListKeys.description],
        [EParameterListKeys.required]:
          !currentItem[EFormParameterListKeys.isOptional],
        [EParameterListKeys.type]: currentItem[EFormParameterListKeys.type],

        ...(currentItem[EFormParameterListKeys.properties] &&
          currentItem[EFormParameterListKeys.type] === 'object' && {
            [EParameterListKeys.properties]:
              getObjectOfPropertiesFromArrayOfProperties(
                currentItem[EFormParameterListKeys.properties] || [],
              ),
          }),

        ...(currentItem[EFormParameterListKeys.type] === 'array' && {
          [EParameterListKeys.properties]: currentItem[
            EFormParameterListKeys.properties
          ]?.map((property) => {
            return {
              [EParameterListKeys.description]:
                property[EFormParameterListKeys.description],
              [EParameterListKeys.required]:
                !property[EFormParameterListKeys.isOptional],
              [EParameterListKeys.type]: property[EFormParameterListKeys.type],
              [EParameterListKeys.name]: property[EFormParameterListKeys.name],
            }
          }),
        }),
      }
      return acc
    },
    {} as Record<string, TParameterListContent>,
  )
}

export const getScenarioActionFormPayloadForApiFromScenarioStep = (
  values: TActionApiCallStep,
) => {
  const newAction: TScenarioActionPayload = {
    [EScenarioActionKeys.name]: values.name || '',
    [EScenarioActionKeys.description]: values.apiActionDescription || '',
    [EScenarioActionKeys.waitMessage]: values.waitMessage || '',
    [EScenarioActionKeys.webhook]: values.webhook || '',
    [EScenarioActionKeys.parametersList]:
      getObjectOfPropertiesFromArrayOfProperties(values.parametersList || []),

    [EScenarioActionKeys.failureMessage]: values.failureMessage || '',
    [EScenarioActionKeys.allPropertiesRequired]: true,
    [EScenarioActionKeys.usesTemplate]: EScenarioActionUsesTemplate.NO,
    [EScenarioActionKeys.method]:
      values.httpMethod || EScenarioActionMethod.GET,
    [EScenarioActionKeys.authHeader]: values.authHeader || '',
    [EScenarioActionKeys.apiKey]: values.apiKey || '',
    [EScenarioActionKeys.headers]: JSON.stringify(
      (values.headers ?? []).reduce(
        (acc, header) => {
          acc[header.key] = header.value
          return acc
        },
        {} as Record<string, string>,
      ),
    ),
  }

  return newAction
}

function parseParameterProperties(
  propertiesObj: Record<string, TParameterListContent> | undefined,
): TFormParameterListContent[] {
  if (!propertiesObj) return []

  return Object.entries(propertiesObj).map(([name, param]) => {
    const base: TFormParameterListContent = {
      [EFormParameterListKeys.id]: nanoid(),
      [EFormParameterListKeys.name]: name,
      [EFormParameterListKeys.description]: param.description,
      [EFormParameterListKeys.isOptional]: !param.required,
      [EFormParameterListKeys.type]: Array.isArray(param.type)
        ? param.type[0]
        : param.type,
    }

    // Recursively parse nested properties for 'object'
    const { properties } = param as unknown as TFormParameterListContent
    if (base[EFormParameterListKeys.type] === 'object' && properties) {
      base[EFormParameterListKeys.properties] = parseParameterProperties(
        properties as unknown as Record<string, TParameterListContent>,
      )
    }

    // Recursively parse nested properties for 'array'
    if (
      base[EFormParameterListKeys.type] === 'array' &&
      Array.isArray(properties)
    ) {
      base[EFormParameterListKeys.properties] = (properties ?? []).map(
        (property) => {
          return {
            [EFormParameterListKeys.id]: nanoid(),
            [EFormParameterListKeys.name]: property.name,
            [EFormParameterListKeys.description]: property.description,
            [EFormParameterListKeys.isOptional]: !(
              property as unknown as TAgentCustomField
            ).required,
            [EFormParameterListKeys.type]: Array.isArray(property.type)
              ? property.type[0]
              : property.type,
          } as TFormParameterListContent
        },
      )
    }

    return base
  })
}

export const getScenarioActionStepFromApiPayload = (
  payload: TScenarioActionProps,
  content: string,
): TActionApiCallStep => {
  // Convert headers JSON string to array of { key, value }
  const headers: { key: string; value: string; id: string }[] = payload.headers
    ? Object.entries(JSON.parse(payload.headers)).map(([key, value]) => ({
        id: nanoid(),
        key,
        value: value as string,
      }))
    : []

  // Convert parametersList object to array
  const parametersList: TFormParameterListContent[] = payload.parametersList
    ? parseParameterProperties(
        payload.parametersList as Record<string, TParameterListContent>,
      )
    : []

  return {
    name: payload?.name || '',
    webhook: payload?.webhook || '',
    httpMethod: payload.httpMethod,
    authHeader: payload?.authHeader || '',
    apiKey: payload?.apiKey || '',
    waitMessage: payload?.waitMessage || '',
    failureMessage: payload?.failureMessage || '',
    parametersList,
    headers,
    enableNewApiActions: true,
    apiActionDescription: payload?.description || '',
    id: payload?.id || '',
    type: EActionStepType.API_CALL,
    description: content,
  }
}

export const getInstructionStepsScenarioActionSteps = (
  actionSteps: TActionStep[],
) => {
  return actionSteps.reduce(
    (acc, step, index) => {
      acc[index + 1] = step?.description || ''
      return acc
    },

    {} as Record<number, string>,
  )
}

export const getActionIdsFromInstructions = (
  instructions: string,
  actions: TScenarioAction[],
) => {
  const regex = /\{\{(.*?)\}\}/g
  const matches = instructions.match(regex)

  return (matches || []).reduce((acc, currentMatch) => {
    const correspondingId = actions.find(
      (action) => `{{${action[EScenarioActionKeys.name]}}}` === currentMatch,
    )?.[EScenarioActionKeys.id]

    if (correspondingId && !acc.includes(correspondingId)) {
      acc.push(correspondingId)
    }

    return acc
  }, [] as string[])
}

export const getPartsFromAction = (line: string) =>
  line.split(/(\{\{[^}]+\}\})/).filter(Boolean)

export const getInstructionStepsFromForm = (
  formInstructionSteps: string,
): Record<number, string> => {
  const steps = formInstructionSteps
    .split('\n')
    .map((step) => step.replace(/^\d+\.\s*/, ''))
    .filter((step) => step.trim() !== '')
    .reduce(
      (acc, step, index) => {
        acc[index + 1] = step
        return acc
      },
      {} as Record<number, string>,
    )
  return steps
}

export const getFormInstructionsStepsFromScenario = (
  instructionSteps: TInstructionStep[] | undefined | null,
): string => {
  return (instructionSteps || [])
    .sort((a, b) => {
      return a.stepNumber - b.stepNumber
    })
    .map((step) => `${step.stepNumber}. ${step.content}\n`)
    .join('')
}

export const getScenarioTitlePayloadWithDefaults = (
  scenario?: Partial<TScenarioPayload> | null,
): TUpdateScenarioTitlePayload => ({
  [EScenarioKeys.name]: scenario?.[EScenarioKeys.name] || DEFAULT_SCENARIO_NAME,
})

export const getScenarioPayloadWithDefaults = (
  scenario?: Partial<TScenario> | null,
): TUpdateScenarioPayload => ({
  ...scenario,
  [EScenarioKeys.instructionSteps]: getFormInstructionsStepsFromScenario(
    scenario?.[EScenarioKeys.instructionStep],
  ),
  [EScenarioKeys.additionalInstructions]:
    scenario?.[EScenarioKeys.additionalInstructions] || '',
  [EScenarioKeys.triggerInstructions]:
    scenario?.[EScenarioKeys.triggerInstructions] || '',
  [EScenarioKeys.agentCustomFields]:
    Object.entries(scenario?.[EScenarioKeys.agentCustomFields] || {}).map(
      ([key, value]) => ({
        [EFormAgentCustomFieldKeys.key]: key,
        ...value,
      }),
    ) || [],
  [EScenarioKeys.channels]: scenario?.[EScenarioKeys.channels] || [],
  [EScenarioKeys.actionIds]: scenario?.[EScenarioKeys.actionIds] || [],
})

export const getNumberedInstructions = (
  instructions: string,
  isEmptyLineAllowed: boolean = false,
) => {
  return instructions
    .split('\n')
    .filter((line) => !/^\d+\.$/.test(line)) // Remove lines that are just a number and a dot
    .filter(
      (line) => isEmptyLineAllowed || line.replace(/^\d+\.\s*/, '') !== '',
    ) // Remove empty lines if not allowed
    .filter(
      (line, index, arr) =>
        !(
          line.replace(/^\d+\.\s*/, '') === '' &&
          arr[index - 1]?.replace(/^\d+\.\s*/, '') === ''
        ),
    )
    .map((line, index) => {
      const cleanLine = line.replace(/^\d+\.\s*/, '') // Remove existing numbers from the start of the line
      return `${index + 1}. ${cleanLine}`
    })
    .join('\n')
}
